import { React, useState, useContext, useEffect, useMemo, useRef } from "react";
import { Flex, Heading, Text, MTag, Divider, Select, Button, Breadcrumb, Container, MRadio, MNotification, Modal, StatusIcon } from "@cisco/spaces-dash-lib-styleguide";
import { Code as MCode } from "@magnetic/code";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { useNotification } from "@cisco/spaces-dash-app-business-hierarchy";
import { AppContext } from "../../contexts/AppContext";
import { PropertiesContext } from "../../contexts/propertiesContext";
import * as PartnerHomeService from "../../service/partner-dashboard-service";
import "./firehose-trace.scss";

function FirehoseTrace(props) {
  const { isPartnerDashboard: isPartnerDashboardProp, i18n, navigateTo } = props;
  const [isPartnerDashboard, setIsPartnerDashboard] = useState(false);
  const { properties } = useContext(PropertiesContext);
  const [streamingInProgress, setStreamingInProgress] = useState(false);
  const [streamingInProgressModal, setStreamingInProgressModal] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [selectedAmPm, setSelectedAmPm] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [, setStartTime] = useState(null);
  const [duration, setDuration] = useState(null);
  const [availableDurations, setAvailableDurations] = useState([]);
  const [availableHours, setAvailableHours] = useState([]);
  const [availableMinutes, setAvailableMinutes] = useState([]);
  const isReadEvents = useRef(false);

  const updateStartTime = (date, hour, minute, ampm) => {
    if (date && hour && minute !== null && ampm) {
      const timeString = `${date} ${hour}:${minute} ${ampm}`;
      const newStartTime = moment(timeString, "YYYY-MM-DD HH:mm A").valueOf();
      setStartTime(newStartTime);
    }
  };

  const onDateSelect = (value) => {
    setSelectedDate(value);
    setSelectedHour(null); // Reset hour when date changes
    setSelectedMinute(null); // Reset minute when date changes
    setSelectedAmPm(null); // Reset AM/PM when date changes
    setDuration(null); // Reset duration when date changes
    updateStartTime(value, null, null, null);
  };

  // Create minutes and durations based on selected date, hour, and minute
  const updateMinutesAndDurations = (selectedDt, selectedHr, selectedMin = null) => {
    const now = moment();
    const minutes = [];
    const durations = [];
    const nowLocal = moment();

    if (selectedDt && selectedHr) {
      const selectedDateTime = moment(`${selectedDt} ${selectedHr}`, "YYYY-MM-DD HH");
      const isCurrentHour = selectedDateTime.format("YYYY-MM-DD HH") === nowLocal.format("YYYY-MM-DD HH");
      const maxMinute = isCurrentHour ? nowLocal.minute() : 59;

      // Create minutes options (0 to maxMinute)
      for (let i = 0; i <= maxMinute; i++) {
        minutes.push({
          label: i.toString().padStart(2, "0"),
          value: i
        });
      }

      // Update durations if minute is selected
      if (selectedMin !== null) {
        const selectedFullDateTime = moment(`${selectedDt} ${selectedHr}:${selectedMin}`, "YYYY-MM-DD HH:mm");
        if (selectedFullDateTime && selectedFullDateTime.isValid()) {
          const maxDuration = now.diff(selectedFullDateTime, "minutes");
          if (maxDuration > 0) {
            // Create minute-by-minute duration options up to maxDuration or 60 minutes
            const durationLimit = Math.min(60, maxDuration);
            for (let i = 1; i <= durationLimit; i++) {
              durations.push({
                label: i === 1 ? "1 minute" : `${i} minutes`,
                value: i
              });
            }
          }
        }
      }
    }

    setAvailableMinutes(minutes);
    setAvailableDurations(durations);
  };

  const onHourSelect = (value) => {
    const selectedHourOption = availableHours.filter((h) => h.date === selectedDate).find((h) => h.value === value);

    if (selectedHourOption) {
      setSelectedHour(value);
      setSelectedAmPm(selectedHourOption.ampm);
      setSelectedMinute(null); // Reset minute when hour changes
      setDuration(null); // Reset duration when hour changes
      updateMinutesAndDurations(selectedDate, value);
      updateStartTime(selectedDate, value, null, selectedHourOption.ampm);
    }
  };

  const onMinuteSelect = (value) => {
    setSelectedMinute(value);
    setDuration(null); // Reset duration when minute changes
    updateMinutesAndDurations(selectedDate, selectedHour, value); // Update available durations with selected minute
    updateStartTime(selectedDate, selectedHour, value, selectedAmPm);
  };

  const [app, setApp] = useState(null);
  const [customerActivations, setCustomerActivations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const customerSelected = useRef(null);
  const partnerTenantIdSelected = useRef(null);
  const [activations, setActivations] = useState([]);
  const streamUUID = useRef("");
  const [dataType, setDataType] = useState("historical");
  const [traceLogs, setTraceLogs] = useState(null);
  const { setToastMessage } = useNotification();

  let partnerApp = null;
  let activationData = [];
  const OPTIONS_FOR_STREAMS = {
    credentials: "include",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    referrerPolicy: "no-referrer",
    responseType: "text",
    reportProgress: true,
    observe: "events",
    method: "GET"
  };
  const totalNoOfRecordsReceived = useRef(0);
  const presentDataRecordLimit = useRef(0);
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);
  const [isActivationSelected, setIsActivationSelected] = useState(false);
  const eventData = useRef([]);

  const appContextValue = useMemo(() => ({ isPartnerDashboard }), [isPartnerDashboard]);

  const backTo = (path) => {
    navigateTo(path);
  };

  const stopStream = () => {
    PartnerHomeService.stopTrace(streamUUID.current)
      .then((data) => {
        console.log(data);
        setStreamingInProgress(false);
        isReadEvents.current = false;
      })
      .catch((error) => {
        console.error(`FirhoseTrace: Error on stopStream ${error}`);
      });
  };

  const handleError = (error) => {
    console.log(error);
    setToastMessage(i18n.t("partner-dashboard-app.partner-apps.firehose-trace.error-on-streaming-live"), 5000, "negative");
  };

  const prepareTimeSelections = () => {
    const now = moment();
    const startDate = moment().subtract(1, "days");
    const dates = [];
    const hours = [];

    // Group hours by date
    while (startDate <= now) {
      const currentDate = startDate.format("YYYY-MM-DD");
      const dateLabel = startDate.format("MMM D");
      const hoursForDate = [];

      // Get all hours for the current date until now
      while (startDate.format("YYYY-MM-DD") === currentDate && startDate <= now) {
        hoursForDate.push({
          label: startDate.format("hh"),
          value: startDate.format("HH"),
          ampm: startDate.format("A"),
          date: currentDate,
          dateLabel,
          timestamp: startDate.valueOf()
        });
        startDate.add(1, "hour");
      }

      if (hoursForDate.length > 0) {
        dates.push({
          label: dateLabel,
          value: currentDate,
          timestamp: moment(currentDate).valueOf()
        });
        hours.push(...hoursForDate);
      }
    }

    setAvailableHours(hours);
  };

  const startStream = () => {
    setStreamingInProgress(true);
    isReadEvents.current = true;
    setTraceLogs("Streaming...");
    const uuid = uuidv4();
    streamUUID.current = uuid;
    eventData.current = [];
    let fullText = "";
    // let totalChunksReceived = 0;
    totalNoOfRecordsReceived.current = 0;

    fetch(`/api/v1/partnerapps/firehose/streamdata?appId=${app.appId}&uniqueId=${uuid}&partnerTenantId=${partnerTenantIdSelected.current}`, OPTIONS_FOR_STREAMS)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        function readChunk({ done, value }) {
          if (!isReadEvents.current || done) {
            reader.cancel();
            return;
          }

          const chunkText = decoder.decode(value, { stream: true });
          const events = chunkText.split("\n").filter((event) => event); // Split and filter non-empty lines

          events.forEach((event) => {
            if (event.includes("200:")) {
              totalNoOfRecordsReceived.current += 1;
              eventData.current.push(event.split("200:")[1]);
            } else if (event.includes("404:") || event.includes("04:")) {
              // totalNoOfRecordsReceived.current += 1;
              const timestamp = moment().format("hh:mm:ss a, D MMMM YYYY");
              eventData.current.push(`${timestamp} : No events found`);
            } else {
              totalNoOfRecordsReceived.current += 1;
              eventData.current.push(event);
            }
          });

          // Limit to last 30 records
          if (eventData.current.length > 30) {
            eventData.current.splice(0, eventData.current.length - 30);
          }

          fullText = eventData.current.join("\r\n");
          setTraceLogs(fullText);

          if (totalNoOfRecordsReceived.current >= presentDataRecordLimit.current) {
            setToastMessage(i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-data-stream-record-stopped-text"), 5000, "warning");
            stopStream();
          }

          reader.read().then(readChunk).catch(handleError);
        }

        reader.read().then(readChunk).catch(handleError);
      })
      .catch(handleError);
  };

  const getRecordLength = () => {
    console.log(`getRecordLength >>${eventData.current.length}`);
    if (eventData.current && eventData.current.length && eventData.current.length <= totalNoOfRecordsReceived.current) {
      return eventData.current.length;
    }
    return 0;
  };

  const getPartnerAppDetails = (appId) => {
    // PartnerHomeService.getAppStrapiDetails(appId)
    PartnerHomeService.getAppDetails(appId)
      .then((data) => {
        if (data.status && data.data) {
          partnerApp = data.data;
          setApp(partnerApp);
          if (partnerApp && (partnerApp.applicationType === "On-Prem" || partnerApp.applicationType === "STC")) {
            setIsActivationSelected(false);
          } else {
            setIsActivationSelected(true);
          }
        }
      })
      .catch((error) => {
        console.error(`FirhoseTrace: Error on getPartnerAppDetails ${error}`);
      });
  };

  const prepareCustomerActivations = (factivations) => {
    const customerList = factivations.map((data, index) => ({
      id: index,
      name: data.customer,
      label: data.customer,
      value: index
    }));

    setCustomers(customerList);
  };

  const getCustomersAndActivations = (appId) => {
    PartnerHomeService.getCustomersAndActivations(appId)
      .then((data) => {
        if (data.status && data.data) {
          activationData = data.data;
          setCustomerActivations(activationData);
          prepareCustomerActivations(activationData);
        }
      })
      .catch((error) => {
        console.error(`FirhoseTrace: Error on getCustomersAndActivations ${error}`);
      });
  };

  const stayHere = () => {
    setStreamingInProgressModal(false);
  };

  const stopStreamAndSwitch = () => {
    PartnerHomeService.stopTrace(streamUUID.current)
      .then((data) => {
        console.log(data);
        setStreamingInProgress(false);
        isReadEvents.current = false;
        setStreamingInProgressModal(false);
        setDataType("historical");
        setStartTime(null);
        setDuration(null);
      })
      .catch((error) => {
        setStreamingInProgressModal(false);
        console.error(`FirhoseTrace: Error on stopStream ${error}`);
      });
  };

  const onCustomerSelect = (id) => {
    const activationList = [];
    if (id !== undefined && id !== null) {
      setIsCustomerSelected(true);
      customerSelected.current = id;
      const factivations = customerActivations && customerActivations[id] ? customerActivations[id].activations : [];
      if (factivations && factivations.length) {
        const newActivationList = factivations.map((activation) => {
          const otherInfoArr = [
            {
              key: "Activation ID",
              value: activation.activationRefId ? activation.activationRefId : ""
            },
            {
              key: "Partner Tenant ID",
              value: activation.partnerTenantId ? activation.partnerTenantId : ""
            }
          ];
          const dropdownObj = {
            name: activation.activationRefId,
            label: `Activation ID: ${activation.activationRefId}\r\n Partner Tenant ID: ${activation.partnerTenantId}`,
            value: activation.partnerTenantId,
            otherInfo: otherInfoArr
          };
          return {
            ...dropdownObj,
            ...activation
          };
        });

        activationList.push(...newActivationList);
      }
      setActivations(activationList);
    } else {
      setIsCustomerSelected(false);
      customerSelected.current = null;
      setActivations([]);
      partnerTenantIdSelected.current = null;
    }
  };

  const firehoseTypeChangeConfirmationModal = () => {
    return (
      <Modal size="md" isOpen={streamingInProgressModal}>
        <Flex direction="vertical" gap={24}>
          <Flex direction="vertical" gap={32} align="center">
            <StatusIcon status="warning" height={48} width={48} />
            <Flex justify="center" direction="vertical" gap={12}>
              <Flex justify="center">
                <div style={{ textAlign: "center" }}>{i18n.t("partner-dashboard-app.partner-apps.firehose-trace.present-data-stream-inprogress-text")}</div>
              </Flex>
              <Flex justify="center">
                <div style={{ textAlign: "center" }}> {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.switch-to-historical-data-text")}?</div>
              </Flex>
            </Flex>
          </Flex>
          <Flex justify="space-between" style={{ width: "100%" }}>
            <Button.Group className="modal-footer">
              <Button kind="tertiary" onClick={stayHere}>
                {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.stay-here-text")}
              </Button>
              <Button kind="primary" onClick={stopStreamAndSwitch}>
                {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.stop-stream-switch-historical-text")}
              </Button>
            </Button.Group>
          </Flex>
        </Flex>
      </Modal>
      // <Modal isOpen={streamingInProgressModal}>
      //   <Flex direction="vertical">
      //     <Flex
      //       align="center"
      //       gap={12}
      //       margin={{
      //         bottom: 16
      //       }}
      //     >
      //       <Warning
      //         height={24}
      //         style={{
      //           marginTop: -2
      //         }}
      //         width={24}
      //       />
      //       <Heading as="h2" size="primary">
      //         {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.present-data-stream-inprogress-text")}
      //       </Heading>
      //     </Flex>
      //     <Text
      //       size="p2"
      //       style={{
      //         marginBottom: 24
      //       }}
      //     >
      //       {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.switch-to-historical-data-text")}
      //     </Text>
      //     <Flex justify="flex-end">
      //       <Button.Group>
      //         <Button kind="tertiary" onClick={stayHere}>
      //           {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.stay-here-text")}
      //         </Button>
      //         <Button onClick={stopStreamAndSwitch}>{i18n.t("partner-dashboard-app.partner-apps.firehose-trace.stop-stream-switch-historical-text")}</Button>
      //       </Button.Group>
      //     </Flex>
      //   </Flex>
      // </Modal>
    );
  };

  const onActivationSelect = (id) => {
    if (id) {
      partnerTenantIdSelected.current = id;
      setIsActivationSelected(true);
    } else {
      partnerTenantIdSelected.current = null;
      setIsActivationSelected(false);
    }
  };

  const downloadHist = (params) => {
    window.open(`/api/v1/firehose/download?${params}`, "_blank");
  };

  const onDurationSelect = (value) => {
    setDuration(value);
  };

  // Update downloadHistoricalData to use new time format
  const downloadHistoricalData = () => {
    const uuid = uuidv4();
    let params = "";

    if (selectedDate && selectedHour && selectedMinute !== null && selectedAmPm && duration) {
      const selectedTime = moment(`${selectedDate} ${selectedHour}:${selectedMinute} ${selectedAmPm}`, "YYYY-MM-DD HH:mm A").valueOf();
      const endTime = moment(selectedTime).add(duration, "minutes").valueOf();

      if (app && app.applicationType !== "MTC") {
        if (app.appId && isCustomerSelected && partnerTenantIdSelected.current) {
          params = `appId=${app.appId}&uniqueId=${uuid}&partnerTenantId=${partnerTenantIdSelected.current}&startTimeStamp=${selectedTime}&endTimeStamp=${endTime}`;
        }
      } else {
        params = `appId=${app.appId}&uniqueId=${uuid}&startTimeStamp=${selectedTime}&endTimeStamp=${endTime}`;
      }
    }

    if (params) {
      downloadHist(params);
    } else {
      console.log("Error Occurred");
    }
  };

  const downloadHistoricalContent = () => {
    return (
      dataType === "historical" && (
        <Flex direction="vertical" style={{ width: "100%" }}>
          <Flex direction="vertical" gap={16} style={{ marginTop: "30px" }}>
            <Flex direction="vertical" gap={8} style={{ width: "216px" }}>
              <Text weight="semi-bold">Date</Text>
              <Select
                value={selectedDate}
                onChange={(e) => onDateSelect(e)}
                options={availableHours.reduce((acc, curr) => {
                  if (!acc.find((d) => d.value === curr.date)) {
                    acc.push({
                      label: curr.dateLabel,
                      value: curr.date
                    });
                  }
                  return acc;
                }, [])}
                placeholder="Select date"
              />
            </Flex>

            <Flex justify="horizontal">
              <Flex direction="vertical" gap={8} style={{ width: "100px" }}>
                <Text weight="semi-bold">Hour</Text>
                <Select
                  value={selectedHour}
                  onChange={(e) => onHourSelect(e)}
                  options={availableHours
                    .filter((h) => h.date === selectedDate)
                    .map((h) => ({
                      label: `${h.label} ${h.ampm}`,
                      value: h.value,
                      ampm: h.ampm
                    }))}
                  placeholder="Hour"
                  disabled={!selectedDate}
                />
              </Flex>
              <Flex className="demarcAlignStyle">:</Flex>
              <Flex direction="vertical" gap={8} style={{ width: "100px" }}>
                <Text weight="semi-bold">Minute</Text>
                <Select value={selectedMinute} onChange={(e) => onMinuteSelect(e)} options={availableMinutes} placeholder="Minute" disabled={!selectedHour} />
              </Flex>
              <Flex className="demarcAlignStyle2">-</Flex>
              <Flex direction="vertical" gap={8} style={{ width: "200px" }}>
                <Text weight="semi-bold">Duration</Text>
                <Select value={duration} onChange={(e) => onDurationSelect(e)} options={availableDurations} placeholder="Duration" disabled={!selectedMinute} />
              </Flex>
            </Flex>
          </Flex>
          <Flex style={{ marginTop: "30px" }}>
            <Button kind="primary" size="md" onClick={downloadHistoricalData} id="start-stream-btn" disabled={!selectedDate || !selectedHour || selectedMinute === null || !selectedAmPm || !duration}>
              <span>{i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-text")}</span>
            </Button>
          </Flex>
        </Flex>
      )
    );
  };

  const downloadRealTimeContent = () => {
    return (
      dataType == "realtime" &&
      isActivationSelected && (
        <Flex direction="vertical" style={{ width: "100%" }}>
          <Flex style={{ "margin-top": "30px" }}>
            {!streamingInProgress && (
              <div style={{ cursor: "pointer" }}>
                <Button kind="primary" size="md" onClick={startStream} id="start-stream-btn">
                  <span>{i18n.t("partner-dashboard-app.partner-apps.firehose-stream-current-data.stream-data-text")}</span>
                </Button>
              </div>
            )}
            {streamingInProgress && (
              <div style={{ cursor: "pointer" }}>
                <Button kind="primary" size="md" onClick={stopStream} id="stop-stream-btn">
                  <span>{i18n.t("partner-dashboard-app.partner-apps.firehose-stream-current-data.stop-stream-text")}</span>
                </Button>
              </div>
            )}
          </Flex>
          {traceLogs && (
            <Flex direction="vertical" style={{ width: "100%" }}>
              <Flex style={{ "margin-top": "10px" }}>
                <Text size="p4" weight="regular" color="light">
                  {`${i18n.t("partner-dashboard-app.partner-apps.firehose-stream-current-data.view-last-text")}`}
                </Text>
                &nbsp;
                <Text size="p4" weight="regular" color="light" id="firehose-record-length">
                  {getRecordLength()}
                </Text>
                &nbsp;
                <Text size="p4" weight="regular" color="light">
                  {`${i18n.t("partner-dashboard-app.partner-apps.firehose-stream-current-data.records-of-text")}`}
                </Text>
                &nbsp;
                <Text size="p4" weight="regular" color="light">
                  {totalNoOfRecordsReceived.current}
                </Text>
                &nbsp;
                <Text size="p4" weight="regular" color="light">
                  {`${i18n.t("partner-dashboard-app.partner-apps.firehose-stream-current-data.record-streamed-text")}`}
                </Text>
              </Flex>
              {streamingInProgress && (
                <Flex style={{ "margin-top": "20px" }} id="firehose-streaming-started">
                  <MNotification status="informational" id="trace-info-message">
                    <Flex id="trace-info-message-flex">
                      <Text size="p3" weight="semi-bold">
                        {`${i18n.t("partner-dashboard-app.partner-apps.app-center.note-text")}: `}
                      </Text>
                      &nbsp;
                      <Text size="p4" weight="regular" color="light">
                        {`${i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-data-stream-record-text")}`}
                      </Text>
                    </Flex>
                  </MNotification>
                </Flex>
              )}
              <Flex style={{ width: "100%", height: "400px" }} className="trace-block">
                {traceLogs && (
                  <MCode.Block
                    id="trace-firhose-code-block"
                    fontSize="sm"
                    style={{
                      "margin-top": "10px",
                      width: "100%",
                      height: "400px"
                    }}
                    height={400}
                    width={900}
                  >
                    {traceLogs}
                  </MCode.Block>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      )
    );
  };

  const chooseFireHoseTraceType = (val) => {
    if (streamingInProgress) {
      setStreamingInProgressModal(true);
    } else {
      setStreamingInProgressModal(false);
      setDataType(val?.target?.value);
      setStartTime(null);
      setDuration(null);
    }
  };

  useEffect(() => {
    console.log("FirehoseTrace Mount");
    console.log(properties);
    const url = window.location.href;
    const res = url.split("/");
    const pos = res.indexOf("trace");
    const result = res[pos + 1];
    presentDataRecordLimit.current = parseInt(i18n.t("partner-dashboard-app.partner-apps.firehose-trace.records-limit"), 10);
    if (properties && properties.isFedRampEnv) {
      setDataType("realtime");
    } else {
      setDataType("historical");
    }
    prepareTimeSelections();
    getPartnerAppDetails(result);
    getCustomersAndActivations(result);

    return () => {
      console.log("FirehoseTrace Destroy");
      if (partnerTenantIdSelected.current && streamUUID.current) {
        stopStream();
      }
    };
  }, []);

  useEffect(() => {
    if (isPartnerDashboardProp) {
      setIsPartnerDashboard(true);
    }
  }, [isPartnerDashboardProp, setIsPartnerDashboard]);

  useEffect(() => {
    i18n.loadNamespaces("partners-home");
    i18n.setDefaultNamespace("partners-home");
  }, [i18n]);

  return (
    <AppContext.Provider value={appContextValue}>
      <Breadcrumb style={{ margin: "10px" }}>
        <Breadcrumb.Link
          id="home-link"
          onClick={() => {
            backTo("/");
          }}
        >
          {i18n.t("partner-dashboard-app.partner-apps.app-center.home-text")}
        </Breadcrumb.Link>
        <Breadcrumb.Link>{i18n.t("partner-dashboard-app.partner-apps.firehose-trace.firehose-trace-text")}</Breadcrumb.Link>
      </Breadcrumb>
      {streamingInProgressModal && firehoseTypeChangeConfirmationModal()}
      <Flex style={{ margin: "20px" }}>
        <Heading size="sub-section" id="trace-firehose-heading">
          {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.trace-firehose-text")}
          <br />
        </Heading>
      </Flex>
      {app && app.draftConfig && (
        <Flex style={{ margin: "40px" }}>
          <Container style={{ width: "100%" }}>
            <Flex direction="vertical" gap={16}>
              <Flex>
                <Heading>{app.draftConfig.appName}</Heading>
              </Flex>
              <Flex gap={10}>
                <MTag.Colored size="sm" color={app.draftConfig.applicationType === "On-Prem" ? 1 : 4}>
                  {app.draftConfig.applicationType === "On-Prem" ? "ON_PREM" : "CLOUD"}
                </MTag.Colored>
                <Flex>
                  <Text color="light" size="p4">
                    {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.app-id-text")}:
                  </Text>
                  &nbsp;
                  <Text color="light" size="p4">
                    {app.draftConfig.appId}
                  </Text>
                </Flex>
              </Flex>
              <Flex justify="center" style={{ "margin-right": "10px", "margin-left": "10px" }}>
                <Divider direction="horizontal" strong />
              </Flex>
              {app && (app.applicationType === "On-Prem" || app.applicationType === "STC") && (
                <Flex style={{ "margin-top": "25px", width: "100%" }} direction="vertical" gap={50}>
                  <Flex direction="vertical" gap={10}>
                    <Text weight="semi-bold">{i18n.t("partner-dashboard-app.partner-apps.firehose-trace.customer-text")}</Text>
                    <Select
                      id="firehose-customer-name-selection-dropdown"
                      fixedWidth
                      clearable
                      onChange={onCustomerSelect}
                      options={customers}
                      placeholder="Select Customer"
                      value={customerSelected.current}
                      searchable
                    />
                  </Flex>
                  <Flex direction="vertical" gap={8}>
                    <Flex direction="vertical" gap={10}>
                      <Text weight="semi-bold">{i18n.t("partner-dashboard-app.partner-apps.firehose-trace.activation-text")}</Text>
                      <Select
                        clearable
                        fixedWidth
                        id="firehose-activation-selection-dropdown"
                        onChange={onActivationSelect}
                        options={activations}
                        placeholder="Select Activation"
                        value={partnerTenantIdSelected.current}
                        searchable
                      />
                    </Flex>
                    {!isCustomerSelected && (
                      <Flex style={{ width: "600px" }}>
                        <MNotification status="warning" width={600} style={{ width: "600px" }}>
                          {i18n.t("partner-dashboard-app.partner-apps.firehose-trace.please-select-customer")}
                        </MNotification>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              )}
              {isActivationSelected && (
                <Flex style={{ "margin-top": "25px", width: "100%" }} id="trace-firehose-radio-options-flex">
                  {properties && properties.isFedRampEnv ? (
                    <MRadio
                      id="trace-firehose-radio-options"
                      direction="vertical"
                      legend={i18n.t("partner-dashboard-app.partner-apps.firehose-trace.streaming-data-text")}
                      style={{ width: "100%" }}
                      onChange={chooseFireHoseTraceType}
                      value={dataType}
                      options={[
                        {
                          children: downloadRealTimeContent(),
                          description: i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-data-stream-text"),
                          key: "realtime",
                          label: i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-realtime-text"),
                          value: "realtime"
                        }
                      ]}
                      size="md"
                    />
                  ) : (
                    <MRadio
                      id="trace-firehose-radio-options"
                      direction="vertical"
                      legend={i18n.t("partner-dashboard-app.partner-apps.firehose-trace.streaming-data-text")}
                      style={{ width: "100%" }}
                      onChange={chooseFireHoseTraceType}
                      value={dataType}
                      options={[
                        {
                          children: downloadHistoricalContent(),
                          description: i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-from-24-hrs-text"),
                          key: "historical",
                          label: i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-historical-data-text"),
                          value: "historical"
                        },
                        {
                          children: downloadRealTimeContent(),
                          description: i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-data-stream-text"),
                          key: "realtime",
                          label: i18n.t("partner-dashboard-app.partner-apps.firehose-trace.download-realtime-text"),
                          value: "realtime"
                        }
                      ]}
                      size="md"
                    />
                  )}
                </Flex>
              )}
            </Flex>
          </Container>
        </Flex>
      )}
    </AppContext.Provider>
  );
}

export default FirehoseTrace;
